import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Globe,
  Twitter,
  Info,
  Heart,
  MessageSquare,
  ExternalLink,
  CheckCircle,
  Sparkles,
  Copy,
  Check,
  MessageCircle,
  CalendarClock,
} from "lucide-react";

const EngagedLeadCard = React.memo(({ lead, expandedCard, toggleCard }) => {
  const [copied, setCopied] = useState(false);

  // Reset copied state when card changes
  useEffect(() => {
    setCopied(false);
  }, [lead.id]);

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Get the actual text content we want to copy - this should match what's displayed
    const textToCopy =
      lead.details.comment || "You've successfully engaged with this lead. Consider following up if you haven't received a response.";

    console.log("Copying text:", textToCopy);

    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          console.log("Text copied successfully via Clipboard API");
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Clipboard API error:", err);
          // Fall back to execCommand
          fallbackCopy(textToCopy);
        });
    } else {
      // If Clipboard API isn't available, use execCommand
      fallbackCopy(textToCopy);
    }
  };

  // Fallback copy method using document.execCommand
  const fallbackCopy = (text) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);

      // Focus and select the text
      textArea.focus();
      textArea.select();

      // Execute the copy command
      const successful = document.execCommand("copy");

      // Clean up
      document.body.removeChild(textArea);

      if (successful) {
        console.log("Copy successful via execCommand fallback");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("execCommand fallback failed");
        alert("Copy failed. Please try manually selecting and copying the text.");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      alert("Copy failed. Please try manually selecting and copying the text.");
    }
  };

  const getPlatformIcon = (platform) => {
    const platformLower = platform ? platform.toLowerCase() : "";
    if (platformLower === "twitter") {
      return <Twitter size={18} className="text-blue-400" />;
    } else {
      return <Globe size={18} className="text-orange-500" />;
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-lg shadow-sm border border-green-200 overflow-hidden transition-all duration-300"
    >
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {getPlatformIcon(lead.details?.platform || lead.platform)}
              <span className="text-xs font-medium text-gray-600">{lead.details?.platform || lead.platform || "Unknown"}</span>
              {lead.details?.user_handle && <span className="text-xs text-gray-500">{lead.details.user_handle}</span>}
              <span className="text-xs text-gray-400 flex items-center">
                <CalendarClock size={12} className="mr-1" />
                {new Date(lead.engaged_at || lead.created_at).toLocaleDateString()}
              </span>
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Engaged</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {lead.details?.post_title || (lead.original_post ? lead.original_post.title : "Engaged Lead")}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={() => toggleCard(lead.id)} className="p-1 hover:bg-gray-100 rounded-full">
              {expandedCard === lead.id ? (
                <ChevronUp size={20} className="text-gray-500" />
              ) : (
                <ChevronDown size={20} className="text-gray-500" />
              )}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          <p className="text-sm text-gray-700">
            {lead.details?.post_excerpt ||
              (lead.original_post && lead.original_post.details ? lead.original_post.details.content : "No content available")}
          </p>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center text-xs text-gray-500">
            <CheckCircle size={14} className="mr-1 text-green-500" />
            <span>Engaged on {new Date(lead.engaged_at || lead.created_at).toLocaleDateString()}</span>
          </div>

          <div className="flex items-center space-x-3 text-gray-500 text-sm">
            <div className="flex items-center">
              <Heart className="w-4 h-4 mr-1 text-rose-500" />
              <span>{lead.details?.like_count || 0}</span>
            </div>
            <div className="flex items-center">
              <MessageSquare className="w-4 h-4 mr-1 text-blue-500" />
              <span>{lead.details?.comment_count || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expandedCard === lead.id && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-200"
          >
            <div className="p-5">
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-2 text-green-800 flex items-center">
                  <Sparkles size={18} className="mr-2 text-green-500" />
                  Engagement Details
                </h4>
                <p className="text-gray-700 text-sm">
                  {lead.details?.comment ||
                    "You've successfully engaged with this lead. Consider following up if you haven't received a response."}
                </p>
                <div className="flex justify-end mt-3">
                  <button onClick={handleCopy} className="text-xs flex items-center space-x-1 text-green-600 hover:text-green-700">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span className="ml-1">{copied ? "Copied!" : "Copy text"}</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                {lead.details?.post_url && (
                  <button
                    className="text-sm bg-white text-green-600 border border-green-200 hover:bg-green-50 px-3 py-2 rounded-lg flex items-center"
                    onClick={() => window.open(lead.details.post_url, "_blank")}
                  >
                    <ExternalLink size={14} className="mr-1" />
                    View Original
                  </button>
                )}

                <button
                  className="text-sm bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 px-3 py-2 rounded-lg flex items-center"
                  onClick={() => {
                    /* Could add functionality to mark as "followed up" or other action */
                  }}
                >
                  <CheckCircle size={14} className="mr-1" />
                  Already Engaged
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

export default EngagedLeadCard;
